import React, { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { getFormErrorMessage } from '../../utils/form-error-parser';
import { inputTypes } from '../../utils/helpers/enums';
import { Button } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import TimePicker from 'react-time-picker';
import UsersSelect from '../users-select/users-select.component';
import ProjectsSelect from '../projects-select/projects-select.component';
import TimeAmountPicker from '../time-amount-picker/time-amount-picker.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';

const InputByType = ({ type, registerProps = {}, ...otherProps }) => {
  let registerData = {};

  if (registerProps) {
    registerData = {
      ...registerProps.register(registerProps.name, registerProps.validation),
    };
  }

  switch (type) {
    case inputTypes.select:
      const { options = [], placeholder } = otherProps;

      if (registerProps && registerProps.register) {
        registerProps.register(registerProps.name, {
          pattern: new RegExp(
            `^${options
              .map((option) => option.value.replace('|', '|'))
              .join('|')}$`
          ),
          ...registerProps.validation,
        });
      }

      return (
        <Form.Select
          onChange={(e) => {
            if (registerProps && registerProps.setValue) {
              registerProps.setValue(registerProps.name, e.target.value);
            }
          }}
          {...otherProps}
        >
          {placeholder && <option value={''}>{placeholder}</option>}
          {options.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Form.Select>
      );
    case inputTypes.usersSelect:
      return (
        <UsersSelect
          onChange={(users) => {
            if (registerProps && registerProps.setValue) {
              registerProps.setValue(
                registerProps.name,
                users.map((user) => user._id)
              );
            }
          }}
          {...otherProps}
        />
      );
    case inputTypes.projectsSelect:
      return (
        <ProjectsSelect
          onChange={(projects) => {
            if (registerProps && registerProps.setValue) {
              registerProps.setValue(
                registerProps.name,
                projects.map((project) => project._id)
              );
            }
          }}
          {...otherProps}
        />
      );
    case inputTypes.timePicker:
      return (
        <TimePicker
          onChange={(e) => {
            if (registerProps && registerProps.setValue) {
              registerProps.setValue(registerProps.name, e);
            }
          }}
          value={otherProps.defaultValue}
        />
      );
    case inputTypes.timeAmountPicker:
      return (
        <TimeAmountPicker
          onChange={(e) => {
            if (registerProps && registerProps.setValue) {
              registerProps.setValue(registerProps.name, e);
            }
          }}
          {...otherProps}
        />
      );
    case inputTypes.multiple:
      if (!otherProps.childs || !otherProps.useForm) {
        return null;
      }

      for (const key in otherProps.childs) {
        if (otherProps.childs.hasOwnProperty(key)) {
          otherProps.childs[key].parentName = registerProps.name;
          otherProps.childs[key].dynamicInputs = otherProps.dynamicInputs;
        }
      }

      return (
        <FormComponent
          form={otherProps.childs}
          useForm={otherProps.useForm}
          additionalStyles={{
            flexDirection: 'row',
          }}
          formAdditionalProps={{
            isDynamicForm: otherProps.dynamicInputs,
            parentName: registerProps.name,
            handleDeleteDynamicInput: otherProps.handleDeleteDynamicInput,
            confirmBeforeDeleting: otherProps.confirmBeforeDeleting,
          }}
        />
      );
    default:
      return <Form.Control type={type} {...otherProps} {...registerData} />;
  }
};

const FormComponent = ({
  form = {},
  useForm = {
    register: () => ({}),
    unregister: () => ({}),
    errors: {},
    setValue: (a, b) => {},
    getValues: () => ({}),
  },
  loading = false,
  additionalStyles = {},
  formAdditionalProps = {},
}) => {
  const { register, unregister, errors, setValue, getValues } = useForm;
  const [dynamicInputs, setDynamicInputs] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    const formValues = getValues();
    const toSet = {};

    for (const key in form) {
      if (form.hasOwnProperty(key) && form[key].isDynamic) {
        for (const formKey in formValues) {
          if (formKey !== key) {
            continue;
          }

          if (Array.isArray(formValues[formKey])) {
            toSet[formKey] = formValues[formKey].map((v, index) => index);
          }
        }
      }
    }

    setDynamicInputs(toSet);
  }, [form]);

  const handleDeleteDynamicInput = (name, value) => {
    const newDynamicInputs = { ...dynamicInputs };
    if (!newDynamicInputs[name]) {
      return unregister(`${name}[${value}]`);
    }

    newDynamicInputs[name] = newDynamicInputs[name].filter(
      (input) => input !== value
    );
    setDynamicInputs(newDynamicInputs);
    unregister(`${name}[${value}]`);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        ...additionalStyles,
      }}
    >
      <ConfirmModal
        show={confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          confirmAction();
          setConfirmAction(null);
        }}
      />

      {Object.entries(form).map(
        ([
          key,
          {
            label,
            placeholder,
            type,
            validation = {},
            parentName = '',
            ...otherProps
          },
        ]) => {
          const additionalProps = {};
          const value = getValues()[key];
          if (value) {
            additionalProps.defaultValue = value;
          }

          if (placeholder && placeholder.length > 0) {
            additionalProps.placeholder = placeholder;
          }

          if (type === inputTypes.multiple) {
            additionalProps.useForm = useForm;
            additionalProps.dynamicInputs = dynamicInputs;
            additionalProps.handleDeleteDynamicInput = handleDeleteDynamicInput;
          }

          return (
            <Form.Group
              key={key}
              style={{
                ...(otherProps.hidden ? { display: 'none' } : {}),
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {(!parentName.length ||
                  (otherProps.dynamicInputs &&
                    otherProps.dynamicInputs[parentName] &&
                    otherProps.dynamicInputs[parentName].length > 0)) && (
                  <Form.Label style={{ fontWeight: '500' }}>{label}</Form.Label>
                )}
                {type === inputTypes.multiple && otherProps.isDynamic && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem',
                    }}
                  >
                    <Button
                      variant={'primary'}
                      onClick={() =>
                        setDynamicInputs((prev) => ({
                          ...prev,
                          [key]: prev[key]
                            ? [...prev[key], prev[key].length]
                            : [0],
                        }))
                      }
                      size={'sm'}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>

              {parentName.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  {otherProps.dynamicInputs[parentName] &&
                    otherProps.dynamicInputs[parentName].map((v, index) => (
                      <div key={index}>
                        <InputByType
                          type={type}
                          {...otherProps}
                          disabled={loading}
                          {...additionalProps}
                          registerProps={{
                            name: `${parentName}[${v}].${key}`,
                            register,
                            validation: validation || {},
                            setValue,
                          }}
                        />
                        {errors &&
                          errors[parentName] &&
                          errors[parentName][v] &&
                          errors[parentName][v][key] && (
                            <Form.Text className='text-danger'>
                              {getFormErrorMessage(
                                errors[parentName][v] &&
                                  errors[parentName][v][key],
                                validation
                              )}
                            </Form.Text>
                          )}
                      </div>
                    ))}
                </div>
              ) : (
                <div
                  style={{
                    ...(type === inputTypes.multiple && otherProps.isDynamic
                      ? {
                          maxHeight: '300px',
                          overflowY: 'auto',
                        }
                      : {}),
                  }}
                >
                  <InputByType
                    type={type}
                    {...otherProps}
                    disabled={loading}
                    {...additionalProps}
                    registerProps={{
                      name: key,
                      register,
                      validation: validation || {},
                      setValue,
                    }}
                  />
                </div>
              )}
              {errors[key] && (
                <Form.Text className='text-danger'>
                  {getFormErrorMessage(errors[key], validation)}
                </Form.Text>
              )}
            </Form.Group>
          );
        }
      )}

      {formAdditionalProps.isDynamicForm &&
        formAdditionalProps.isDynamicForm[formAdditionalProps.parentName] &&
        formAdditionalProps.isDynamicForm[formAdditionalProps.parentName]
          .length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Form.Label style={{ fontWeight: '500' }}>Action</Form.Label>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {formAdditionalProps.isDynamicForm[
                formAdditionalProps.parentName
              ].map((v, index) => (
                <div key={index}>
                  <Button
                    variant='danger'
                    onClick={() => {
                      if (formAdditionalProps.confirmBeforeDeleting) {
                        setConfirmAction(() => {
                          return () =>
                            formAdditionalProps.handleDeleteDynamicInput(
                              formAdditionalProps.parentName,
                              v
                            );
                        });
                        return;
                      }

                      formAdditionalProps.handleDeleteDynamicInput(
                        formAdditionalProps.parentName,
                        v
                      );
                    }}
                  >
                    <FaTrash />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default FormComponent;
